import { CustomerProfilData } from "../entities/CustomerProfil";
import { createDefaultReference } from "../entities/defaults";
import {
  Gender,
  Reference,
  Address,
  NextOfKin,
  ProfessionAndIncome,
  // ProofOfId,
  // ProofOfAddress,
  RiskLevel,
  GeoLocation,
} from "../entities/shared";

// Interface pour la réponse de l'API
export interface ApiReference {
  code: string | null;
  name: string | null;
}

export interface ApiCountry {
  id: number;
  code: string;
  name: string;
  fullName: string;
  codeIso2: string;
  codeIso3: string | null;
  codeIsoNum: string;
  region: string;
  sousRegion: string;
  continent: string;
}

export interface ApiGeoLocation {
  id?: string;
  latitude: number;
  longitude: number;
}

export interface CustomerProfilApiResponse {
  id: string;
  businessKey: string;
  customerCode: string | null;
  first_name: string;
  last_name: string;
  gender: "MALE" | "FEMALE" | "OTHER";
  email1: string;
  email1Verified: boolean;
  email2: string | null;
  email2Verified: boolean;
  phone1: string;
  phone1CountryCode: string | null;
  phone1Verified: boolean;
  phone2: string | null;
  phone2CountryCode: string | null;
  phone2Verified: boolean;
  fax?: string | null;
  idPaper: null;

  nationality: ApiReference | null;
  language: ApiReference | null;
  legalCapacity: ApiReference | null;
  familyStatus: ApiReference | null;
  legalSituation: ApiReference | null;

  address: {
    id: string;
    street: string | null;
    postalAddress?: string | null;
    area: string | null;
    city: string | null;
    postalCode: string | null;
    geoLocation: ApiGeoLocation | null;
    country: ApiCountry | null;
  } | null;

  nextOfKins: Array<{
    id: string;
    firstName: string;
    middleName: string | null;
    surname: string;
    gender: string | null;
    email: string | null;
    phone: string | null;
    relationship: string | null;
    address: {
      id: string;
      type: string | null;
      street: string | null;
      postalAddress?: string | null;
      area: string | null;
      city: string | null;
      localGovernment: string | null;
      postalCode: string | null;
      country: ApiCountry | null;
      geoLocation: ApiGeoLocation | null;
    } | null;
  }>;
  status: string;
  /**
   * Raw risk level coming from the API.
   * Can be domain codes (STANDARD/HIGH/LOW) or UI-style codes (DEFAUT/ELEVE/FAIBLE/TRES_ELEVE/MODERE).
   */
  riskLevel: string | null;
  profileRating: number;
  solde: number | null;
  advisor: {
    id: string;
    name: string;
    email: string | null;
  } | null;

  professionAndIncomes: {
    id: string;
    employmentType: ApiReference | null;
    educationLevel: ApiReference | null;
    structureLevel: ApiReference | null;
    profession: string;
    company: string | null;
    employerEmail: string | null;
    hireDate: string | null;
    monthlyIncome: number;
    additionalIncome: number | null;
    incomeSource: string | null;
  } | null;
}

export class CustomerProfilMapper {
  /**
   * Transforme la réponse de l'API en modèle de domaine
   */
  static toDomainModel(
    apiResponse: CustomerProfilApiResponse,
  ): CustomerProfilData {
    return {
      id: apiResponse.id,
      businessKey: apiResponse.businessKey,
      customerCode: apiResponse.customerCode || "",
      firstName: apiResponse.first_name,
      lastname: apiResponse.last_name || "",
      surname: apiResponse.last_name || "",
      segment: "Personne physique", // Default segment, can be updated if API provides it
      gender: this.mapGender(apiResponse.gender),
      email1: apiResponse.email1 ?? "",
      email1Verified: apiResponse.email1Verified ?? false,
      email2: apiResponse.email2 ?? null,
      email2Verified: apiResponse.email2Verified ?? false,
      bvn: "",
      phone1: apiResponse.phone1 ?? "",
      phone1CountryCode: apiResponse.phone1CountryCode ?? null,
      phone1Verified: apiResponse.phone1Verified ?? false,
      phone2: apiResponse.phone2 ?? null,
      phone2CountryCode: apiResponse.phone2CountryCode ?? null,
      phone2Verified: apiResponse.phone2Verified ?? false,
      fax: apiResponse.fax ?? null,
      nationality: this.mapReference(apiResponse.nationality),
      language: this.mapReference(apiResponse.language),
      legalCapacity: this.mapReference(apiResponse.legalCapacity),
      familyStatus: this.mapReference(apiResponse.familyStatus),
      legalSituation: this.mapReference(apiResponse.legalSituation),
      proofOfId: null,
      proofOfAddress: null,
      addresses: this.mapAddress(apiResponse.address),
      nextOfKins: Array.isArray(apiResponse.nextOfKins)
        ? apiResponse.nextOfKins.map((kin) => this.mapNextOfKin(kin))
        : [],
      customProperties: [],
      professionAndIncomes: this.mapProfessionAndIncome(
        apiResponse.professionAndIncomes,
      ),
      profileRating:
        typeof apiResponse.profileRating === "number"
          ? apiResponse.profileRating
          : 0,
      riskLevel:
        apiResponse.riskLevel !== undefined && apiResponse.riskLevel !== null
          ? this.mapRiskLevel(apiResponse.riskLevel)
          : null,
      solde: apiResponse.solde,
      status: this.mapStatus(apiResponse.status),
      pep: false,
      birthDate: null,
      birthPlace: null,
      idPaper: apiResponse.idPaper ?? null,
    };
  }

  /**
   * Mappe une référence API vers le modèle de domaine
   */
  private static mapReference(apiRef: ApiReference | null): Reference {
    if (!apiRef || (!apiRef.code && !apiRef.name)) {
      return createDefaultReference();
    }

    return {
      id: 0,
      code: apiRef.code ?? "",
      label: apiRef.name ?? "",
    };
  }

  /**
   * Mappe le genre de l'API vers le type Gender
   */
  private static mapGender(apiGender: string | null | undefined): Gender {
    if (!apiGender) return "OTHER";
    const genderMap: Record<string, Gender> = {
      MALE: "MALE",
      FEMALE: "FEMALE",
      M: "MALE",
      F: "FEMALE",
      MASCULIN: "MALE",
      FEMININ: "FEMALE",
    };

    const upperGender = apiGender.toUpperCase();
    // Safe property access to prevent prototype pollution
    return (
      (Object.prototype.hasOwnProperty.call(genderMap, upperGender)
        ? genderMap[upperGender]
        : null) || "OTHER"
    );
  }

  /**
   * Mappe le statut de l'API vers le type CustomerStatus
   */
  private static mapStatus(apiStatus: string): "ACTIF" | "INACTIF" | "DORMANT" {
    const statusMap: Record<string, "ACTIF" | "INACTIF" | "DORMANT"> = {
      ACTIF: "ACTIF",
      INACTIF: "INACTIF",
      DORMANT: "DORMANT",
      ACTIVE: "ACTIF",
      INACTIVE: "INACTIF",
    };

    const upperStatus = apiStatus.toUpperCase();
    // Safe property access to prevent prototype pollution
    return (
      (Object.prototype.hasOwnProperty.call(statusMap, upperStatus)
        ? statusMap[upperStatus]
        : null) || "INACTIF"
    );
  }

  /**
   * Mappe le niveau de risque brut de l'API vers le type de domaine RiskLevel.
   */
  private static mapRiskLevel(apiRiskLevel: string | null): RiskLevel {
    if (!apiRiskLevel) return null;

    const riskMap: Record<string, RiskLevel> = {
      STANDARD: "STANDARD",
      MODERE: "STANDARD",
      DEFAUT: "STANDARD",

      HIGH: "HIGH",
      ELEVE: "HIGH",
      TRES_ELEVE: "HIGH",

      LOW: "LOW",
      FAIBLE: "LOW",
    };

    const input = apiRiskLevel.toUpperCase();
    return riskMap[input] ?? null;
  }

  /**
   * Mappe une adresse API vers le modèle de domaine
   */
  private static mapAddress(
    apiAddress: CustomerProfilApiResponse["address"],
  ): Address {
    if (!apiAddress) {
      return {
        id: "",
        type: "HOME",
        street: null,
        postalAddress: null,
        area: null,
        city: null,
        localGovernment: null,
        postalCode: null,
        country: null,
        geoLocation: null,
      };
    }

    return {
      id: apiAddress.id,
      type: "HOME",
      street: apiAddress.street,
      postalAddress: apiAddress.postalAddress ?? null,
      area: apiAddress.area,
      city: apiAddress.city,
      localGovernment: null,
      postalCode: apiAddress.postalCode ?? null,
      country: apiAddress.country
        ? {
            id: apiAddress.country.code,
            code: apiAddress.country.codeIso2,
            label: apiAddress.country.name,
          }
        : null,
      geoLocation: apiAddress.geoLocation
        ? this.mapGeoLocation(apiAddress.geoLocation)
        : null,
    };
  }

  /**
   * Mappe une géolocalisation API vers le modèle de domaine
   */
  private static mapGeoLocation(apiGeoLocation: ApiGeoLocation): GeoLocation {
    return {
      id: apiGeoLocation.id ?? "",
      latitude: apiGeoLocation.latitude,
      longitude: apiGeoLocation.longitude,
    };
  }

  /**
   * Mappe un proche API vers le modèle de domaine
   */
  private static mapNextOfKin(
    apiNextOfKin: CustomerProfilApiResponse["nextOfKins"][number],
  ): NextOfKin {
    return {
      id: apiNextOfKin.id,
      firstName: apiNextOfKin.firstName,
      middleName: apiNextOfKin.middleName,
      surname: apiNextOfKin.surname,
      gender: apiNextOfKin.gender ? this.mapGender(apiNextOfKin.gender) : null,
      email: apiNextOfKin.email,
      phone: apiNextOfKin.phone,
      relationship: apiNextOfKin.relationship,
      address: apiNextOfKin.address
        ? this.mapNextOfKinAddress(apiNextOfKin.address)
        : null,
    };
  }

  /**
   * Mappe une adresse de proche API vers le modèle de domaine
   */
  private static mapNextOfKinAddress(
    apiAddress: CustomerProfilApiResponse["nextOfKins"][number]["address"],
  ): Address {
    if (!apiAddress) {
      return {
        id: "",
        type: "HOME",
        street: null,
        postalAddress: null,
        area: null,
        city: null,
        localGovernment: null,
        postalCode: null,
        country: null,
        geoLocation: null,
      };
    }

    return {
      id: apiAddress.id,
      type: apiAddress.type ?? "HOME",
      street: apiAddress.street,
      postalAddress: apiAddress.postalAddress ?? null,
      area: apiAddress.area,
      city: apiAddress.city,
      localGovernment: apiAddress.localGovernment,
      postalCode: apiAddress.postalCode ?? null,
      country: apiAddress.country
        ? {
            id: apiAddress.country.code,
            code: apiAddress.country.codeIso2,
            label: apiAddress.country.name,
          }
        : null,
      geoLocation: apiAddress.geoLocation
        ? this.mapGeoLocation(apiAddress.geoLocation)
        : null,
    };
  }

  /**
   * Mappe les informations professionnelles API vers le modèle de domaine
   */
  private static mapProfessionAndIncome(
    apiProfession: CustomerProfilApiResponse["professionAndIncomes"],
  ): ProfessionAndIncome | null {
    if (!apiProfession) return null;

    return {
      id: apiProfession.id,
      employmentType: this.mapReference(apiProfession.employmentType),
      educationLevel: this.mapReference(apiProfession.educationLevel),
      structureLevel: this.mapReference(apiProfession.structureLevel),
      profession: apiProfession.profession,
      company: apiProfession.company ?? "",
      employerEmail: apiProfession.employerEmail ?? "",
      hireDate: apiProfession.hireDate ?? "",
      monthlyIncome: apiProfession.monthlyIncome ?? 0,
      additionalIncome: apiProfession.additionalIncome ?? 0,
      incomeSource: apiProfession.incomeSource ?? "",
    };
  }

  /**
   * Mappe une preuve d'identité API vers le modèle de domaine
   */
  // private static mapProofOfId(apiProof: {
  //   id: string;
  //   type: string;
  //   nature: string;
  //   document: string;
  // }): ProofOfId {
  //   return {
  //     id: apiProof.id,
  //     type: apiProof.type,
  //     nature: apiProof.nature,
  //     document: apiProof.document,
  //   };
  // }

  /**
   * Mappe une preuve d'adresse API vers le modèle de domaine
   */
  // private static mapProofOfAddress(apiProof: {
  //   id: string;
  //   type: string;
  //   nature: string;
  //   document: string;
  // }): ProofOfAddress {
  //   return {
  //     id: apiProof.id,
  //     type: apiProof.type,
  //     nature: apiProof.nature,
  //     document: apiProof.document,
  //   };
  // }

  /**
   * Transforme le modèle de domaine vers le format API (si nécessaire)
   */
  static toApiRequest(
    domainModel: Partial<CustomerProfilData>,
  ): Partial<CustomerProfilApiResponse> {
    // Cette méthode peut être utilisée si vous devez transformer le modèle de domaine vers l'API
    // Pour l'instant, retourne le modèle tel quel
    return domainModel as Partial<CustomerProfilApiResponse>;
  }
}
